import Controller from "csi/action/Controller";
import JsonResponse from "csi/comm/JsonResponse";
import Request from "csi/def/Request";
import locale from "csi/locale";
import MessageDialog from "csi/widget/MessageDialog";
import xml from "csi/xml";
import { CallMethod } from "csi/xml/CallMethod";
import declare from "dojo/_base/declare";
import { fetchIsUserProofAdmin } from "../ProofService";

enum DeleteProofUserControllerOperationEnum {
	MarkC8UserInactiveAndDeleteZiflowUser = "MarkC8UserInactiveAndDeleteZiflowUser",
	MarkInactiveZiflowUser = "MarkInactiveZiflowUser",
}

export = declare(Controller, {

	/** @property {Record<DeleteProofUserControllerOperationEnum, string>} operation - operation to call */
	operation: null,

	/** @property {boolean} isUserProofAdmin - indicate if User is Proof Admin */
	isUserProofAdmin: false,

	/** @property {string} saveResourceId - change default Save button to read as Continue */
	saveResourceId: "BUTTON_CONT",

	/**
	 * @override
	 * Get the Admin or Proof Admin status of the user if needed.
	 */
	onPreActionDeferreds(dfds: Promise<JsonResponse>[]): void {
		if (this.operation === DeleteProofUserControllerOperationEnum.MarkInactiveZiflowUser) {
			const newDfd = fetchIsUserProofAdmin(this.m_urlNode)
				.then((res) => this.isUserProofAdmin = res.getReturnValue());
			dfds.push(newDfd);
		}
	},

	/**
	 * @override
	 * Indicate if user has a standard confirm dialog.
	 */
	hasToConfirmAction(): boolean {
		return this.operation === DeleteProofUserControllerOperationEnum.MarkInactiveZiflowUser;
	},

	/**
	 * @override
	 * Setup the confirm dialog. Depending on if the user is a Proof Admin or not, display 1 or 2.
	 */
	confirmAction(params): void {
		const displayName = xml.nodeGetName(this.getNode());
		if (this.isUserProofAdmin) {
			params.message = locale.resource("ProofingLicenseUserConfirmDialog1", [displayName]);
		} else {
			params.message = locale.resource("ProofingLicenseUserConfirmDialog2", [displayName]);
		}
		params.buttons = [MessageDialog.BUTTON_REMOVE + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_CANCEL];
		params.title = locale.resource("RemoveProofingLicenseForUser", [displayName]);
		this.inherited(arguments);
	},

	/**
	 * @override
	 * Continue with action if user clicks on OK.
	 */
	onConfirm(key: string): void {
		if (key === MessageDialog.BUTTON_REMOVE) {
			this.preProcess();
		} else {
			this.postProcess();
		}
	},

	/**
	 * @override
	 * Refresh the data on dialog cancel to show the user is still active.
	 */
	postProcessCancel(): void {
		if (this.operation === DeleteProofUserControllerOperationEnum.MarkC8UserInactiveAndDeleteZiflowUser) {
			this.widget.redrawData();
		}
	},

	/**
	 * @override
	 * Get the request to call when marking a RegisteredZiflowUser inactive.
	 */
	_getActionRequest(): Request {
		if (this.operation) {
			return {
				m_module: "Proofing",
				m_operation: this.operation,
				isValid: () => true,
			};
		}
		return this.inherited<Request>(arguments);
	},

	/**
	 * @override
	 * Call the Ziflow operation to delete the user. Move Proofs to new owner.
	 */
	toWriterRequest(): CallMethod {
		const call = this.inherited<CallMethod>(arguments);

		if (this.operation === DeleteProofUserControllerOperationEnum.MarkC8UserInactiveAndDeleteZiflowUser) {
			const newOwnerCnl = this.m_formData.getFieldValue("ProofingLicenseUsers");
			call.addParameter("NewOwner", xml.ATTR_REF, newOwnerCnl);
		}

		call.addParameter("User", xml.ATTR_REF, this.m_urlNode);

		return call;
	},
});